import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { Box, Package, Heart, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import { Link, useLocation } from "react-router-dom";
import ServicesSection from "../../components/Services";
import Footer from "../../components/Footer";
import { useEffect } from "react";
// import bannerImg from "../../assets/box-banner.png"; // Add your banner image path here

export default function BoxPrinting() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Box Printing
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              We provide high-quality box printing services, including pharmaceutical packaging and custom box printing for a variety of industries. Let's elevate your packaging together!
            </p>
            <Link to="/contact">
            <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Get Started Today
            </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pharma Medicine Box Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Pharma Medicine Box Printing Service
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Capital Offset, we offer high-quality pharma medicine box printing services with precision, care, and attention to detail. We ensure timely execution and the use of the best materials for superior results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pharma Carton Printing */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Pharma Carton Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize in printing pharma cartons that are elegant, functional, and durable. Available in a range of finishes including shiny, non-shiny, half-fold, and tri-fold designs, our pharma cartons are created to meet your specifications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pharmaceutical Box Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Pharmaceutical Box Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our pharmaceutical box printing services are designed to cater to the unique requirements of the pharmaceutical industry. We print boxes with utmost care, ensuring that all information is accurate and clear, and that the final product meets your exact specifications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us for Box Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Why Choose Capital Offset for Box Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Capital Offset is a leading provider of box printing services. We offer customized box printing solutions for the pharmaceutical industry and beyond, with an emphasis on quality, precision, and customer satisfaction.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Box className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">High-Quality Printing</h3>
              <p className="text-gray-600">
                We use advanced printing techniques and high-quality materials to ensure your boxes are printed with precision, vibrant colors, and clarity.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Package className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Custom Designs</h3>
              <p className="text-gray-600">
                We offer full customization to suit your needs, from size and shape to color and finish, ensuring your box perfectly represents your brand.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Heart className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Timely Delivery</h3>
              <p className="text-gray-600">
                We are committed to providing timely delivery for all box printing projects, ensuring you receive your order when you need it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Get Your Boxes Printed Today!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Ready to promote your business with a high-quality catalog? Reach out today to get your boxes printed!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>


      <ServicesSection />
      {/* Contact Form */}
      <ContactSection />
      <Footer />
    </div>
  );
}
